import { useTranslation } from 'react-i18next';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useFailedTransactionsCount, useInboxStats } from 'services/repositories/stats/stats';
import { useCurrentUser } from 'services/repositories/user/user';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { InboxRoutes, TransactionsRoutes } from 'support/types';
import { Page } from 'components/Page/Page';

import { Card } from './components/Card';
import { KnockGuideCard } from './components/KnockGuideCard/KnockGuideCard';
import { OCRNudge } from './components/OCRNudge';

export const InboxDashboard = () => {
  const { t } = useTranslation();
  const { data: currentUser, isLoading: isLoadingCurrentUser } = useCurrentUser();
  const { data: currentPartner, isLoading: isLoadingCurrentPartner } = useCurrentPartner();
  const { data: inboxStatsData, isLoading: loadingInboxStatsData } = useInboxStats({
    staleTime: 0,
    refetchOnMount: true,
  });
  const { data: failedTransactionsData, isLoading: loadingFailedTransactions } = useFailedTransactionsCount();

  const isLoading =
    isLoadingCurrentUser || isLoadingCurrentPartner || loadingInboxStatsData || loadingFailedTransactions;

  const hasInboxPendingActions = inboxStatsData?.data
    ? Object.values(inboxStatsData.data).some((count) => (count ?? 0) > 0)
    : false;

  const hasFailedTransactions = (failedTransactionsData?.actionRequiredTransactions ?? 0) > 0;

  const hasPendingActions = hasInboxPendingActions || hasFailedTransactions;

  const inboxData = inboxStatsData?.data;

  // Counters are only present for the relationship types the partner actually
  // has, so only render a card when its counter exists in the response.
  const inboxCards = (
    [
      { statKey: 'pendingOrderResponses', title: t('dashboard:inbox.cards.missingOrderResponses') },
      { statKey: 'pendingShippingNotices', title: t('dashboard:inbox.cards.missingShippingNotices') },
      { statKey: 'pendingInvoices', title: t('dashboard:inbox.cards.missingInvoices') },
      { statKey: 'pendingDispatchInstructions', title: t('dashboard:inbox.cards.openDispatchInstructions') },
    ] as const
  ).filter((card) => inboxData?.[card.statKey] !== undefined);

  // The blocked-transactions card always renders last. With the two-column grid,
  // an odd total leaves it alone on its row, so let it span the full width.
  const blockedTransactionsSpansFullWidth = (inboxCards.length + 1) % 2 === 1;
  return (
    <Page isLoading={isLoading}>
      <Page.Section>
        <div className="mx-auto -my-6 flex min-h-screen max-w-3xl flex-col gap-8 pt-12 pb-10">
          <div className="flex flex-col">
            <Page.Title>{t('dashboard:inbox.title', { name: currentUser?.name })}</Page.Title>
            <Page.Description>
              {hasPendingActions
                ? t('dashboard:inbox.subtitle.pendingActions')
                : t('dashboard:inbox.subtitle.noPendingActions')}
            </Page.Description>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {inboxCards.map((card) => (
              <Card
                key={card.statKey}
                value={inboxData?.[card.statKey] ?? 0}
                title={card.title}
                to={routeToPage(InboxRoutes.root, {}, { type: card.statKey })}
              />
            ))}
            <Card
              value={failedTransactionsData?.actionRequiredTransactions ?? 0}
              title={t('dashboard:inbox.cards.blockedTransactions')}
              to={TransactionsRoutes.blocked}
              className={blockedTransactionsSpansFullWidth ? 'col-span-2' : undefined}
            />
          </div>

          {currentPartner?.unattendedOcrEmail ? <OCRNudge email={currentPartner.unattendedOcrEmail} /> : null}

          <div className="flex-1">{/* expander */}</div>
          <KnockGuideCard />
        </div>
      </Page.Section>
    </Page>
  );
};
